import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import {
  Users, Activity, Compass, RefreshCcw, AlertTriangle, Store, Clock, MousePointerClick, CreditCard,
} from 'lucide-react';

/**
 * Adopcion (superadmin)
 * ---------------------------------------------------------------------------
 * Responde tres preguntas del negocio con datos que ya emite la app:
 *   1. Altas      — quien se registro, cuando, que rubro y plan eligio, y si
 *                   llego a operar (primera venta) o quedo en la nada.
 *   2. Uso        — que modulos abre cada organizacion (navigation_logs).
 *   3. Onboarding — donde se traba la gente (onboarding_events).
 *
 * Todo el agregado vive en vistas SQL (superadmin_*) para no bajarse decenas de
 * miles de eventos al browser. Las vistas ya filtran por is_superadmin(), asi
 * que un usuario comun recibe vacio aunque llegue a la ruta.
 */

const RUBROS = {
  comercio: 'Comercio / kiosco',
  gastronomia: 'Gastronomía',
  indumentaria: 'Indumentaria',
  servicios: 'Servicios',
};

const formatNumber = (value) => Number(value || 0).toLocaleString('es-AR');

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch {
    return '—';
  }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

// Mediana y no promedio: con pocas altas, una org que tardo tres semanas en
// vender corre el promedio y hace parecer que nadie activa.
const median = (values) => {
  const nums = values.filter((v) => Number.isFinite(Number(v))).map(Number).sort((a, b) => a - b);
  if (nums.length === 0) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
};

const formatHoras = (horas) => {
  if (horas === null || horas === undefined) return '—';
  const n = Number(horas);
  if (!Number.isFinite(n)) return '—';
  if (n < 1) return `${Math.round(n * 60)} min`;
  if (n < 48) return `${n.toFixed(1)} h`;
  return `${Math.round(n / 24)} días`;
};

// Antiguedad legible: en dias mientras el numero se entiende de un vistazo, en
// meses cuando "412 d" ya no dice nada.
const formatAntiguedad = (dias) => {
  const n = Number(dias);
  if (!Number.isFinite(n)) return '—';
  if (n <= 0) return 'hoy';
  if (n < 60) return `${n} d`;
  const meses = Math.floor(n / 30);
  return meses < 24 ? `${meses} meses` : `${Math.floor(n / 365)} años`;
};

const formatHace = (dias) => {
  const n = Number(dias);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return 'hoy';
  if (n === 1) return 'ayer';
  return `hace ${n} d`;
};

function Kpi({ icon: Icon, label, value, hint, tone = 'mint' }) {
  const toneMap = {
    mint: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    zinc: 'bg-zinc-50 text-zinc-700 border-zinc-200',
  };
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${toneMap[tone]}`}>
          <Icon size={14} />
        </div>
      </div>
      <div className="text-[22px] font-black text-zinc-900 leading-tight">{value}</div>
      {hint && <p className="text-[10px] text-zinc-500 mt-1">{hint}</p>}
    </div>
  );
}

function EmptyState({ titulo, detalle }) {
  return (
    <div className="bg-white rounded-xl border border-dashed border-zinc-300 p-8 text-center">
      <AlertTriangle size={22} className="mx-auto mb-2 text-amber-500" />
      <p className="text-sm font-bold text-zinc-800">{titulo}</p>
      {detalle && <p className="text-xs text-zinc-500 mt-1 max-w-lg mx-auto">{detalle}</p>}
    </div>
  );
}

const Th = ({ children, className = '' }) => (
  <th className={`px-3 py-2 text-left text-[10px] font-black uppercase tracking-wider text-zinc-500 ${className}`}>
    {children}
  </th>
);

const Td = ({ children, className = '' }) => (
  <td className={`px-3 py-2 text-xs text-zinc-700 whitespace-nowrap ${className}`}>{children}</td>
);

function Tabla({ children }) {
  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-x-auto">
      <table className="min-w-full divide-y divide-zinc-200">{children}</table>
    </div>
  );
}

const TABS = [
  { id: 'altas', label: 'Altas', icon: Store },
  { id: 'uso', label: 'Uso por módulo', icon: MousePointerClick },
  { id: 'onboarding', label: 'Onboarding', icon: Compass },
];

export default function Adopcion() {
  const [tab, setTab] = useState('altas');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [altas, setAltas] = useState([]);
  const [uso, setUso] = useState([]);
  const [onbOrgs, setOnbOrgs] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [pasos, setPasos] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, u, o, f, p] = await Promise.all([
        supabase.from('superadmin_altas').select('*').order('alta_at', { ascending: false }).limit(500),
        supabase.from('superadmin_uso_modulos').select('*').order('visitas', { ascending: false }).limit(500),
        supabase.from('superadmin_onboarding_orgs').select('*').order('ultimo_evento_at', { ascending: false }).limit(500),
        supabase.from('onboarding_funnel_weekly').select('*').limit(12),
        supabase.from('onboarding_tour_steps_weekly').select('*').limit(200),
      ]);

      const primerError = [a, u, o, f, p].find((r) => r.error)?.error;
      if (primerError) throw primerError;

      setAltas(a.data || []);
      setUso(u.data || []);
      setOnbOrgs(o.data || []);
      setFunnel(f.data || []);
      setPasos(p.data || []);
    } catch (err) {
      console.error('[Adopcion] error cargando datos', err);
      setError(err?.message || 'No pudimos cargar los datos de adopción.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // --- KPIs de altas -------------------------------------------------------
  const kpis = useMemo(() => {
    const hace30 = Date.now() - 30 * 24 * 3600 * 1000;
    const recientes = altas.filter((o) => o.alta_at && new Date(o.alta_at).getTime() >= hace30);
    const activadas = altas.filter((o) => Number(o.ventas || 0) > 0);
    const recientesActivadas = recientes.filter((o) => Number(o.ventas || 0) > 0);
    const tasa = recientes.length
      ? Math.round((recientesActivadas.length / recientes.length) * 100)
      : null;

    const conAbono = altas.filter((o) => Number(o.pagos || 0) > 0);

    return {
      total: altas.length,
      recientes: recientes.length,
      activadas: activadas.length,
      tasa,
      medianaHoras: median(altas.map((o) => o.horas_hasta_primera_venta)),
      // "Nunca entraron" = sin una sola señal de actividad de ningún tipo.
      sinEntrar: altas.filter((o) => !o.ultima_conexion_at).length,
      activas7d: altas.filter((o) => Number(o.dias_desde_ultima_conexion) <= 7).length,
      conAbono: conAbono.length,
      pagosTotales: conAbono.reduce((acc, o) => acc + Number(o.pagos || 0), 0),
      medianaDiasConectados: median(altas.map((o) => o.dias_conectados)),
    };
  }, [altas]);

  // --- Ranking de modulos --------------------------------------------------
  const rankingModulos = useMemo(() => {
    const acc = new Map();
    uso.forEach((row) => {
      const prev = acc.get(row.modulo) || { modulo: row.modulo, visitas: 0, orgs: 0, usuarios: 0 };
      prev.visitas += Number(row.visitas || 0);
      prev.orgs += 1;
      prev.usuarios += Number(row.usuarios || 0);
      acc.set(row.modulo, prev);
    });
    return Array.from(acc.values()).sort((a, b) => b.visitas - a.visitas);
  }, [uso]);

  // --- Pasos mas trabados --------------------------------------------------
  const pasosTrabados = useMemo(
    () => [...pasos]
      .filter((p) => Number(p.trabas || 0) > 0 || Number(p.abandonos || 0) > 0)
      .sort((a, b) => (Number(b.trabas || 0) + Number(b.abandonos || 0)) - (Number(a.trabas || 0) + Number(a.abandonos || 0)))
      .slice(0, 15),
    [pasos]
  );

  const nombrePorOrg = useMemo(() => {
    const map = new Map();
    altas.forEach((o) => map.set(o.organization_id, o.nombre));
    return map;
  }, [altas]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-56 bg-zinc-200 rounded" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 bg-zinc-100 rounded-xl" />)}
          </div>
          <div className="h-64 bg-zinc-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-zinc-900 flex items-center gap-2">
            <Activity size={20} className="text-emerald-600" />
            Adopción
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Quién se registra, qué usa y dónde se traba.
          </p>
        </div>
        <button
          type="button"
          onClick={cargar}
          className="inline-flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50"
        >
          <RefreshCcw size={13} /> Actualizar
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-xs">
          {error}
        </div>
      )}

      <div className="flex gap-1 border-b border-zinc-200">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border-b-2 -mb-px transition ${
              tab === id
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {/* ---------------------------------------------------------------- */}
      {tab === 'altas' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <Kpi icon={Store} label="Altas totales" value={formatNumber(kpis.total)}
              hint={`${formatNumber(kpis.recientes)} en los últimos 30 días`} />
            <Kpi icon={Users} label="Llegaron a vender" value={formatNumber(kpis.activadas)} tone="blue"
              hint={kpis.tasa === null ? 'Sin altas recientes' : `${kpis.tasa}% de las altas de 30 días`} />
            <Kpi icon={Clock} label="Alta → 1ª venta" value={formatHoras(kpis.medianaHoras)} tone="amber"
              hint="Mediana de las que activaron" />
            <Kpi icon={Activity} label="Activas (7 días)" value={formatNumber(kpis.activas7d)} tone="blue"
              hint={`Mediana de ${formatNumber(kpis.medianaDiasConectados)} días conectados`} />
            <Kpi icon={CreditCard} label="Pagaron el abono" value={formatNumber(kpis.conAbono)} tone="mint"
              hint={`${formatNumber(kpis.pagosTotales)} pagos acumulados`} />
            <Kpi icon={AlertTriangle} label="Nunca entraron" value={formatNumber(kpis.sinEntrar)} tone="red"
              hint="Sin una sola señal de actividad" />
          </div>

          {altas.length === 0 ? (
            <EmptyState
              titulo="No hay altas para mostrar"
              detalle="Si estás logueado como superadmin y ves esto, revisá que tu usuario tenga role='superadmin' en la tabla users."
            />
          ) : (
            <Tabla>
              <thead className="bg-zinc-50">
                <tr>
                  <Th>Negocio</Th><Th>Rubro</Th><Th>Plan</Th>
                  <Th>Alta</Th><Th>Antigüedad</Th>
                  <Th>Última conexión</Th><Th>Días conectado</Th>
                  <Th>Usuarios</Th><Th>Productos</Th><Th>Ventas</Th>
                  <Th>Alta → 1ª venta</Th><Th>Pagos</Th><Th>Estado</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {altas.map((o) => (
                  <tr key={o.organization_id} className="hover:bg-zinc-50">
                    <Td className="font-bold text-zinc-900">{o.nombre || '—'}</Td>
                    <Td>{RUBROS[o.rubro] || o.rubro || '—'}</Td>
                    <Td className="uppercase">{o.plan || '—'}</Td>
                    <Td>{formatDate(o.alta_at)}</Td>
                    <Td>{formatAntiguedad(o.dias_desde_alta)}</Td>
                    <Td className={!o.ultima_conexion_at ? 'text-zinc-400' : ''}>
                      {o.ultima_conexion_at ? (
                        <>
                          {formatDate(o.ultima_conexion_at)}
                          <span className={`ml-1.5 text-[10px] ${
                            Number(o.dias_desde_ultima_conexion) > 14 ? 'text-red-600 font-bold' : 'text-zinc-400'
                          }`}>
                            {formatHace(o.dias_desde_ultima_conexion)}
                          </span>
                        </>
                      ) : 'nunca'}
                    </Td>
                    <Td>{formatNumber(o.dias_conectados)}</Td>
                    <Td>{formatNumber(o.usuarios)}</Td>
                    <Td>{formatNumber(o.productos)}</Td>
                    <Td className={Number(o.ventas || 0) > 0 ? 'font-bold text-emerald-700' : 'text-zinc-400'}>
                      {formatNumber(o.ventas)}
                    </Td>
                    <Td>{formatHoras(o.horas_hasta_primera_venta)}</Td>
                    <Td
                      className={Number(o.pagos || 0) > 0 ? 'font-bold text-emerald-700' : 'text-zinc-400'}
                      title={o.ultimo_pago_at ? `Último pago: ${formatDate(o.ultimo_pago_at)}` : 'Sin pagos registrados'}
                    >
                      {formatNumber(o.pagos)}
                    </Td>
                    <Td>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        o.status === 'trial' ? 'bg-amber-50 text-amber-700'
                          : o.status === 'active' ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-zinc-100 text-zinc-600'
                      }`}>
                        {o.status || '—'}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {tab === 'uso' && (
        <div className="space-y-4">
          {uso.length === 0 ? (
            <EmptyState
              titulo="Todavía no hay navegación registrada"
              detalle="La bitácora de navegación se empieza a llenar con los ingresos posteriores a este cambio: cada usuario deja una fila por módulo que abre (deduplicada por ventana de 5 minutos). Volvé en unas horas de uso real."
            />
          ) : (
            <>
              <Tabla>
                <thead className="bg-zinc-50">
                  <tr><Th>Módulo</Th><Th>Visitas</Th><Th>Organizaciones</Th><Th>Usuarios</Th></tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {rankingModulos.map((m) => (
                    <tr key={m.modulo} className="hover:bg-zinc-50">
                      <Td className="font-bold text-zinc-900">{m.modulo}</Td>
                      <Td>{formatNumber(m.visitas)}</Td>
                      <Td>{formatNumber(m.orgs)}</Td>
                      <Td>{formatNumber(m.usuarios)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Tabla>

              <h2 className="text-xs font-black uppercase tracking-widest text-zinc-500 pt-2">
                Detalle por organización
              </h2>
              <Tabla>
                <thead className="bg-zinc-50">
                  <tr>
                    <Th>Negocio</Th><Th>Módulo</Th><Th>Visitas</Th><Th>Usuarios</Th><Th>Última vez</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {uso.map((r) => (
                    <tr key={`${r.organization_id}-${r.modulo}`} className="hover:bg-zinc-50">
                      <Td className="font-bold text-zinc-900">
                        {r.organizacion || nombrePorOrg.get(r.organization_id) || '—'}
                      </Td>
                      <Td>{r.modulo}</Td>
                      <Td>{formatNumber(r.visitas)}</Td>
                      <Td>{formatNumber(r.usuarios)}</Td>
                      <Td>{formatDateTime(r.ultima_visita_at)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Tabla>
            </>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {tab === 'onboarding' && (
        <div className="space-y-4">
          <h2 className="text-xs font-black uppercase tracking-widest text-zinc-500">
            Embudo por semana
          </h2>
          {funnel.length === 0 ? (
            <EmptyState titulo="Sin eventos de onboarding en el período" />
          ) : (
            <Tabla>
              <thead className="bg-zinc-50">
                <tr>
                  <Th>Semana</Th><Th>Altas</Th><Th>Provisionadas</Th><Th>Tour iniciado</Th>
                  <Th>Tour completo</Th><Th>1ª venta real</Th><Th>Checklist</Th><Th>Con trabas</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {funnel.map((w) => (
                  <tr key={w.week} className="hover:bg-zinc-50">
                    <Td className="font-bold text-zinc-900">{formatDate(w.week)}</Td>
                    <Td>{formatNumber(w.signups)}</Td>
                    <Td>{formatNumber(w.provisioned)}</Td>
                    <Td>{formatNumber(w.tour_started)}</Td>
                    <Td>{formatNumber(w.tour_completed)}</Td>
                    <Td className="font-bold text-emerald-700">{formatNumber(w.first_real_sale)}</Td>
                    <Td>{formatNumber(w.checklist_completed)}</Td>
                    <Td className={Number(w.tours_con_traba || 0) > 0 ? 'font-bold text-red-600' : ''}>
                      {formatNumber(w.tours_con_traba)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          )}

          <h2 className="text-xs font-black uppercase tracking-widest text-zinc-500 pt-2">
            Dónde se traba la gente
          </h2>
          {pasosTrabados.length === 0 ? (
            <EmptyState titulo="Ningún paso registró trabas ni abandonos" />
          ) : (
            <Tabla>
              <thead className="bg-zinc-50">
                <tr>
                  <Th>Paso</Th><Th>Tour</Th><Th>Vistas</Th><Th>Completados</Th>
                  <Th>Trabas</Th><Th>Abandonos</Th><Th>Semana</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {pasosTrabados.map((p, i) => (
                  <tr key={`${p.week}-${p.tour_id}-${p.step_id}-${i}`} className="hover:bg-zinc-50">
                    <Td className="font-bold text-zinc-900">{p.step_id}</Td>
                    <Td>{p.tour_id}</Td>
                    <Td>{formatNumber(p.vistas)}</Td>
                    <Td>{formatNumber(p.completados)}</Td>
                    <Td className="font-bold text-red-600">{formatNumber(p.trabas)}</Td>
                    <Td className="font-bold text-amber-600">{formatNumber(p.abandonos)}</Td>
                    <Td>{formatDate(p.week)}</Td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          )}

          <h2 className="text-xs font-black uppercase tracking-widest text-zinc-500 pt-2">
            Estado por organización
          </h2>
          {onbOrgs.length === 0 ? (
            <EmptyState titulo="Sin organizaciones con eventos de onboarding" />
          ) : (
            <Tabla>
              <thead className="bg-zinc-50">
                <tr>
                  <Th>Negocio</Th><Th>Último evento</Th><Th>Último paso</Th>
                  <Th>Paso más trabado</Th><Th>Trabas</Th><Th>1ª venta</Th><Th>Checklist</Th><Th>Cuándo</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {onbOrgs.map((o) => (
                  <tr key={o.organization_id} className="hover:bg-zinc-50">
                    <Td className="font-bold text-zinc-900">
                      {nombrePorOrg.get(o.organization_id) || o.organization_id?.slice(0, 8) || '—'}
                    </Td>
                    <Td>{o.ultimo_evento || '—'}</Td>
                    <Td>{o.ultimo_step || '—'}</Td>
                    <Td className={o.step_mas_trabado ? 'font-bold text-red-600' : 'text-zinc-400'}>
                      {o.step_mas_trabado || '—'}
                    </Td>
                    <Td>{formatNumber(o.trabas)}</Td>
                    <Td>{o.hizo_primera_venta ? '✓' : '—'}</Td>
                    <Td>{o.completo_checklist ? '✓' : '—'}</Td>
                    <Td>{formatDateTime(o.ultimo_evento_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          )}
        </div>
      )}
    </div>
  );
}
